import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { trackUserBehavior } from '../utils/analytics';

const HeroSection = styled.section`
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.8) 100%);
    z-index: 1;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      #00D4FF 20%, 
      #0099CC 50%, 
      #00D4FF 80%, 
      transparent 100%);
    box-shadow: 0 0 20px #00D4FF;
  }

  @keyframes showroomGlow {
    0% { opacity: 0.3; }
    100% { opacity: 0.7; }
  }
`;

const BackgroundVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    repeating-linear-gradient(
      45deg,
      transparent 0px,
      transparent 50px,
      rgba(255,255,255,0.02) 50px,
      rgba(255,255,255,0.02) 52px
    );
  z-index: 2;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 3;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 40px;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: 30px;
  }
`;

const Content = styled.div`
  max-width: 600px;
  
  @media (max-width: 1024px) {
    max-width: 100%;
    text-align: center;
    margin: 0 auto;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    max-width: 100%;
    text-align: center;
  }
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(90deg, ${props => props.theme.colors.primary}20, ${props => props.theme.colors.secondary}20);
  border: 1px solid ${props => props.theme.colors.primary}40;
  border-radius: 50px;
  padding: 8px 20px;
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 12px;
    padding: 6px 16px;
  }
`;

const Title = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.display};
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
  background: linear-gradient(135deg, ${props => props.theme.colors.white} 0%, ${props => props.theme.colors.primary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.lightGray};
  margin-bottom: 32px;
  line-height: 1.6;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const CTAContainer = styled(motion.div)`
  display: flex;
  gap: 16px;
  margin-bottom: 40px;
  justify-content: center;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryCTA = styled(motion.button)`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: ${props => props.theme.colors.dark};
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px ${props => props.theme.colors.primary}40;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100%;
    font-size: 1rem;
    padding: 14px 24px;
  }
`;

const SecondaryCTA = styled(motion.button)`
  background: transparent;
  color: ${props => props.theme.colors.white};
  border: 2px solid ${props => props.theme.colors.primary};
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.primary}20;
    transform: translateY(-2px);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100%;
    font-size: 1rem;
    padding: 12px 24px;
  }
`;

const Stats = styled(motion.div)`
  display: flex;
  gap: 40px;
  justify-content: center;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 20px;
    flex-wrap: wrap;
  }
`;

const Stat = styled.div`
  text-align: left;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    text-align: center;
  }
`;

const StatNumber = styled.div`
  font-family: ${props => props.theme.fonts.display};
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  line-height: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.lightGray};
  margin-top: 4px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
`;

const VideoContainer = styled(motion.div)`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: ${props => props.theme.colors.darkGray};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const LeonardoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center bottom;
  border-radius: 16px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    object-position: center center;
  }
`;

const PlayButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 0 10px 30px ${props => props.theme.colors.primary}40;
  }

  &:after {
    content: '';
    width: 0;
    height: 0;
    border-left: 20px solid ${props => props.theme.colors.dark};
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    margin-left: 4px;
  }
`;

const Hero = () => {
  // Timer de 24h a partir do primeiro acesso do usuário
  const getEndDate = () => {
    let endDate = localStorage.getItem('offerEndDate');
    if (!endDate) {
      // Primeira visita: define 24h a partir de agora
      endDate = new Date().getTime() + (24 * 60 * 60 * 1000);
      localStorage.setItem('offerEndDate', endDate);
    }
    return parseInt(endDate);
  };
  
  const calculateTimeLeft = () => {
    const now = new Date().getTime();
    const difference = getEndDate() - now;
    
    if (difference <= 0) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    
    return {
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000)
    };
  };
  
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleCTAClick = (type) => {
    if (type === 'primary') {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
    } else if (type === 'secondary') {
      const aboutSection = document.querySelector('[id="about"]') || document.querySelector('section:nth-of-type(4)');
      if (aboutSection) {
        aboutSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <HeroSection>
      <BackgroundVideo
        autoPlay
        muted
        loop
        playsInline
      >
        <source src="/videobmw.mp4" type="video/mp4" />
      </BackgroundVideo>
      <BackgroundPattern />
      <Container>
        <Content>
          <Badge
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            OFERTA LIMITADA - {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
          </Badge>
          
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            TRANSFORME SUA LOJA EM UMA MÁQUINA DE LUCRO
          </Title>
          
          <Subtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Aprenda o método CCP - Consignação com Propósito que pode transformar sua loja de veículos em um negócio mais lucrativo e organizado.
          </Subtitle>
          
          <CTAContainer
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <PrimaryCTA
              onClick={() => handleCTAClick('primary')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              QUERO APRENDER O MÉTODO CCP
            </PrimaryCTA>
            <SecondaryCTA
              onClick={() => handleCTAClick('secondary')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              CONHECER LEO CELINI
            </SecondaryCTA>
          </CTAContainer>
          
          <Stats
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <Stat>
              <StatNumber>7+</StatNumber>
              <StatLabel>Anos de Loja Física</StatLabel>
            </Stat>
            <Stat>
              <StatNumber>3</StatNumber>
              <StatLabel>Módulos Completos</StatLabel>
            </Stat>
            <Stat>
              <StatNumber>100%</StatNumber>
              <StatLabel>Validado na LM Motors</StatLabel>
            </Stat>
          </Stats>
        </Content>
        
        <VideoContainer
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
        >
          <LeonardoImage 
            src="/Feliz no celular.jpg" 
            alt="Leonardo Celini - Especialista em CCP"
          />
          {/* TODO: Descomentar quando tiver o vídeo de apresentação do curso
          <PlayButton
            onClick={() => handleCTAClick('video')}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          />
          */}
        </VideoContainer>
      </Container>
    </HeroSection>
  );
};

export default Hero;